import { Plus, Webhook } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { DataTable, useDataTable } from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import type { WebhookEndpoint } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { DeleteEndpointDialog } from '../components/delete-endpoint-dialog';
import type { EndpointAction } from '../components/endpoint-actions-menu';
import { useEndpointColumns } from '../components/endpoint-columns';
import { EndpointDialog } from '../components/endpoint-dialog';
import { endpointFilters } from '../components/endpoint-filters';
import { useEndpointRowActions } from '../components/endpoint-row-actions';
import { RotateSecretDialog } from '../components/rotate-secret-dialog';
import { useWebhookEndpoints } from '../hooks/use-webhook-endpoints';
import { useUpdateWebhook } from '../hooks/use-webhook-mutations';

const EMPTY: readonly WebhookEndpoint[] = [];

type Editing = { open: false } | { open: true; endpoint: WebhookEndpoint | null };

function Webhooks() {
  const { t } = useTranslation('webhooks');
  const store = useStore();
  const message = useErrorMessage();
  const query = useWebhookEndpoints(store.id);
  const toggle = useUpdateWebhook(store.id, 'webhooks:toasts.statusChanged');
  const [editing, setEditing] = useState<Editing>({ open: false });
  const [rotating, setRotating] = useState<WebhookEndpoint | null>(null);
  const [deleting, setDeleting] = useState<WebhookEndpoint | null>(null);
  const endpoints = query.data ?? EMPTY;

  const { mutate: toggleStatus } = toggle;
  const onAction = useCallback(
    (action: EndpointAction, endpoint: WebhookEndpoint) => {
      switch (action) {
        case 'edit':
          setEditing({ open: true, endpoint });
          return;
        case 'toggle':
          toggleStatus(
            {
              id: endpoint.id,
              body: { status: endpoint.status === 'active' ? 'disabled' : 'active' },
            },
            { onError: (error) => toast.error(message(error)) },
          );
          return;
        case 'rotate':
          setRotating(endpoint);
          return;
        case 'delete':
          setDeleting(endpoint);
      }
    },
    [message, toggleStatus],
  );

  const columns = useEndpointColumns(store.id);
  const filters = useMemo(() => endpointFilters(t, endpoints), [endpoints, t]);
  const rowActions = useEndpointRowActions({
    onAction,
    busyId: toggle.isPending ? toggle.variables.id : null,
  });
  const table = useDataTable({
    data: endpoints,
    columns,
    filters,
    getRowId: (row) => row.id,
    initialColumnVisibility: { description: false },
  });

  // The route already requires webhooks.manage; only a closed store makes this read-only.
  const { editable, reason } = useStoreEditable({ permission: [] });
  const addButton = (tour?: 'settings.webhooks.add') =>
    editable ? (
      <Button data-tour={tour} onClick={() => setEditing({ open: true, endpoint: null })}>
        <Plus aria-hidden />
        {t('create.action')}
      </Button>
    ) : null;

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description', { store: store.name })}
        backTo={{ to: '/org/stores/$storeId', params: { storeId: store.id } }}
        backLabel={t('back')}
        actions={addButton('settings.webhooks.add')}
      />
      <ReadOnlyBanner reason={reason} />
      <Alert className="mb-4" data-tour="settings.webhooks.intro">
        <Webhook aria-hidden />
        <AlertDescription>{t('intro')}</AlertDescription>
      </Alert>
      {query.data && endpoints.length === 0 ? (
        <EmptyState
          icon={Webhook}
          title={t('empty.title')}
          description={t('empty.description')}
          action={addButton()}
          tour="settings.webhooks.empty"
          tourLink
        />
      ) : (
        <DataTable
          table={table}
          search={{ label: t('search'), placeholder: t('search') }}
          rowActions={editable ? rowActions : undefined}
          tour={{ table: 'settings.webhooks.table' }}
          getRowLabel={(row) => row.url}
          resourceName={{ singular: t('resource_one'), plural: t('resource_other') }}
          loading={query.isPending}
          fetching={query.isFetching || toggle.isPending}
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      )}
      {editing.open ? (
        <EndpointDialog
          key={editing.endpoint?.id ?? 'new'}
          storeId={store.id}
          endpoint={editing.endpoint}
          open
          onOpenChange={(open) => {
            if (!open) setEditing({ open: false });
          }}
        />
      ) : null}
      <RotateSecretDialog
        storeId={store.id}
        endpoint={rotating}
        onOpenChange={(open) => {
          if (!open) setRotating(null);
        }}
      />
      <DeleteEndpointDialog
        storeId={store.id}
        endpoint={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      />
    </PageContent>
  );
}

export function WebhooksPage() {
  return (
    <FeatureErrorBoundary name="webhooks.list">
      <Webhooks />
    </FeatureErrorBoundary>
  );
}
